import { useEffect, useMemo, useState } from 'react'
import { getAvatarGradientClass, getAvatarInitials } from '../../lib/avatar'

type UserAvatarProps = {
  src?: string | null
  name?: string | null
  alt?: string
  containerClassName?: string
  imgClassName?: string
  iconClassName?: string
  fallbackClassName?: string
}

function cx(...parts: Array<string | undefined | null | false>) {
  return parts.filter(Boolean).join(' ')
}

export default function UserAvatar({
  src,
  name,
  alt = 'Avatar',
  containerClassName,
  imgClassName,
  iconClassName,
  fallbackClassName,
}: UserAvatarProps) {
  const normalizedSrc = useMemo(() => {
    if (typeof src !== 'string') return ''
    return src.trim()
  }, [src])
  const [imageFailed, setImageFailed] = useState(false)
  const [loadSeed] = useState(() => Date.now())

  useEffect(() => {
    setImageFailed(false)
  }, [normalizedSrc])

  const shouldShowImage = Boolean(normalizedSrc) && !imageFailed
  const initialsSeed = name || alt
  const initials = useMemo(() => getAvatarInitials(initialsSeed), [initialsSeed])
  const gradientClass = useMemo(
    () => getAvatarGradientClass(initialsSeed, loadSeed),
    [initialsSeed, loadSeed]
  )

  return (
    <div
      className={cx(
        'overflow-hidden flex items-center justify-center',
        !shouldShowImage && (fallbackClassName || `bg-gradient-to-br ${gradientClass}`),
        containerClassName
      )}
    >
      {shouldShowImage ? (
        <img
          src={normalizedSrc}
          alt={alt}
          className={cx('h-full w-full object-cover', imgClassName)}
          onError={() => setImageFailed(true)}
        />
      ) : null}
      {!shouldShowImage ? (
        <span className={cx('font-semibold text-white uppercase select-none leading-none', iconClassName)}>
          {initials}
        </span>
      ) : null}
    </div>
  )
}
